/* eslint-disable react/no-array-index-key */
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { InteractionManager, Text as NativeText, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { RouteProp, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import {
    endOfDay,
    isToday,
    millisecondsToSeconds,
    secondsToMilliseconds,
    startOfDay,
} from 'date-fns';
import debounce from 'lodash.debounce';

import {
    EPG,
    EPGChannel,
    EPGChannels,
    EPGProgram,
    EPGPrograms,
    EPGRequestPriority,
    IEPG,
} from 'custom_modules/react-native-epg';

import EPGSkeletonLoadingMobile from 'assets/images/animation/epg_loading_mobile.svg';
import EPGSkeletonLoadingTablet from 'assets/images/animation/epg_loading_tablet.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    EPG_PROGRAM_CELLS_LOADING_ID,
    FALLBACK_MESSAGE_ID,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import {
    selectAppState,
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import {
    getEpgByVisibleChannels,
    setEpgDate,
    setFocusToNow,
} from 'store/epg/actions';
import { EpgRequestedBy } from 'store/epg/reducer';
import { EpgLayout } from 'store/epg/reducer.types';
import {
    getEpgSelectedLayout,
    getFocusToNow,
    getNewDayIsLoading,
    selectEpgParams,
    selectEpgPlayerFullscreen,
    selectEpgProgramsByChannelsIds,
    selectEpgRequestStatus,
    selectPreviouslyWatchedChannel,
} from 'store/epg/selectors';
import { StreamTypes } from 'store/player/actions.types';
import {
    selectChannelsById,
    selectChannelsListWithTvChannel,
} from 'store/programs/selectors';
import { EpgProgram, RBChannel } from 'store/railsBuilder/reducer.types';
import { selectEpgTemplateId } from 'store/railsBuilder/selectors';
import { selectCachedRecordingsByEventId } from 'store/recordings/selectors';
import {
    endSentryTransaction,
    startSentryTransaction,
} from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import {
    selectActiveUserProfile,
    selectCatchupCapability,
    selectEntitlements,
} from 'store/user/selectors';
import { isCapabilityValid, requestFailure, requestSuccess } from 'store/utils';

import Announcement from 'services/accessibility/Announcement';
import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';
import isCatchupChannel from 'services/data-manager/catchup/isCatchupChannel';
import isCatchupEpgProgram from 'services/data-manager/catchup/isCatchupEpgProgram';
import { TECHNICAL_DEVICE_TYPE } from 'services/data-manager/constants';
import parseVideoItem from 'services/data-manager/parsing/parseVideoItem';
import parseParentalRating from 'services/data-manager/parsing/utils/parseParentalRating';
import { VideoItem } from 'services/data-manager/types';
import useGetDeepLinkTriggeredParams from 'services/uav/useGetDeepLinkTriggeredParams';
import useThirdPartyAppStartParams from 'services/uav/useGetThirdPartyAppStartParams';
import { logUavAnalytics } from 'services/uav/utils';

import { COLOR } from 'utils/colors';
import useBackHandler from 'utils/hooks/useBackHandler.ts';
import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import usePrevious from 'utils/hooks/usePrevious';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';
import isValidPastDeeplinkItem from 'utils/isValidPastDeeplinkItem';
import openDeeplink from 'utils/openDeeplink';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import {
    heightPercentageToDP,
    isTablet,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

import { DetailsRequestUrlType } from 'screens/Detail/types';
import EpgGridDatePicker from 'screens/Epg/components/EpgGridDatePicker';
import useEpgConfig from 'screens/Epg/hooks/useEpgConfig';
import useAudioAndSubtitlesDialog from 'screens/PlayerModal/hooks/useAudioAndSubtitlesDialog';
import { underlayingPlayerModalElementsDataSet } from 'screens/PlayerModal/hooks/useModalAccessibility.ts';
import useParentalControl from 'screens/PlayerModal/hooks/useParentalControl';
import { SwitchPlayerSource } from 'screens/PlayerModal/types';

import { EArrowType } from 'components/CustomButtons/ArrowButton';
import ScrollArrowButtons from 'components/CustomButtons/ScrollArrowButtons';
import EmbeddedPlayer from 'components/EmbeddedPlayer';
import { EMBEDDED_PLAYER_WEB_ID } from 'components/EmbeddedPlayer/constants';
import usePlayerState from 'components/EmbeddedPlayer/hooks/usePlayerState';
import { IEmbeddedPlayer } from 'components/EmbeddedPlayer/types';
import GenericPinDialog from 'components/GenericPinDialog';
import Notification from 'components/Notification';
import { AppNotification } from 'components/Notification/types';
import {
    DeepLinkTriggered,
    ThirdPartyAppStartItem,
} from 'components/Ribbon/types';
import SkeletonLoading from 'components/SkeletonLoading';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import useAutoScrollOnTimeline from '../../hooks/useAutoScrollOnTimeline';
import useChannels from '../../hooks/useChannels';
import useDatePicker from '../../hooks/useDatePicker';
import useFiltering from '../../hooks/useFiltering';
import useFindLiveProgram from '../../hooks/useFindLiveProgram';
import useShowFullscreenPlayer from '../../hooks/useShowFullscreenPlayer';
import { EpgChannelExtra, EpgProgramExtra } from '../../types';
import { HOURS_IN_VIEWPORT, edges } from '../../utils/config';
import parseRBProgramToEpgProgram from '../../utils/parseRBProgramToEpgProgram';
import { isLive, isLiveEpgProgram } from '../../utils/program';
import EpgChannel from '../EpgChannel';
import EpgDesktopSkeletonLoading from '../EpgDesktopSkeletonLoading';
import EpgFilter from '../EpgFilter';
import EpgHourCell from '../EpgHourCell';
import EpgJumpMarkers from '../EpgJumpMarkers';
import EpgLoadingRow from '../EpgLoadingRow';
import EpgMissingDataCell from '../EpgMissingDataCell';
import EpgNoDataRow from '../EpgNoDataRow';
import EpgNowLine from '../EpgNowLine';
import EpgProgramCell from '../EpgProgramCell';
import EpgTimeBarArrows from '../EpgTimeBarArrows';
import styles from './styles';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.EPG>;
    route: RouteProp<RouteNavigationParams, SCREEN.EPG>;
};

const { CARDS_TITLE_CONTENT_LOCKED } = LANGUAGE_KEYS;

function EpgGridContent({ navigation, route }: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const {
        playerState: { videoItem, playbackType },
        setPlayerState,
    } = usePlayerState({
        videoItem: undefined,
        playbackType: StreamTypes.live,
    });

    const epgRef = useRef<IEPG | null>(null);
    const embeddedPlayerRef = useRef<IEmbeddedPlayer | null>(null);

    const appState = useSelector(selectAppState);
    const catchupCapability = useSelector(selectCatchupCapability);
    const templateId = useSelector(selectEpgTemplateId);
    const recordingsByEventId = useSelector(selectCachedRecordingsByEventId);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);
    const epgSelectedLayout = useSelector(getEpgSelectedLayout);

    const fullscreen = useSelector(selectEpgPlayerFullscreen);
    const { start: epgDataStartTime, requestedBy } =
        useSelector(selectEpgParams);
    const programsByChannelsIds = useSelector(selectEpgProgramsByChannelsIds);
    const channelsList = useSelector(selectChannelsListWithTvChannel);
    const channelsById = useSelector(selectChannelsById);

    const status = useSelector(selectEpgRequestStatus);
    const previouslyWatchedChannels = useSelector(
        selectPreviouslyWatchedChannel,
    );
    const { background, primary = COLOR.primary } =
        useSelector(selectLayoutConfig);
    const entitlements = useSelector(selectEntitlements);
    const profileParentalControlRating = useProfileParentalRating();
    const focusToNow = useSelector(getFocusToNow);
    const startTime = secondsToMilliseconds(epgDataStartTime);
    const selectedProfileId = useSelector(selectActiveUserProfile);
    const newDayIsLoading = useSelector(getNewDayIsLoading);

    const [epgNotification, setEpgNotification] = useState<
        AppNotification | undefined
    >(undefined);
    const [errorExists, setErrorExists] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const setScrollPositionDebounced = useRef(debounce(setScrollPosition, 100));
    const [markersVisible, setMarkersVisible] = useState<boolean>(false);
    const [epgY, setEpgY] = useState<number | undefined>(undefined);
    const [focused, setFocused] = useState(false);
    const [epgPlayerSource, setEpgPlayerSource] = useState<
        | {
              channel: RBChannel;
              program: EpgProgram | undefined;
          }
        | undefined
    >(undefined);
    const [selectedChannelId, setSelectedChannelId] = useState<
        string | undefined
    >(undefined);
    const [selectedProgramData, setSelectedProgramData] =
        useState<EpgProgram | null>(null);
    const [isEPGSelectedChannel, setIsEPGSelectedChannel] = useState(false);

    const previouslyWatchedChannel = selectedProfileId
        ? previouslyWatchedChannels[selectedProfileId]
        : undefined;
    const channel = epgPlayerSource?.channel;

    const setMarkersVisibleDebounced = useRef(
        debounce(setMarkersVisible, 1000),
    );
    const prevProgramsByChannelsIds = useRef(programsByChannelsIds);
    const prevEpgSelectedLayout = usePrevious(epgSelectedLayout);
    const prevScreenReaderEnabled = usePrevious(screenReaderEnabled);

    const {
        selectedFilterCategory,
        filterBottomSheetRef,
        filterCategories,
        isFilterListVisible,
        filterCategorySelected,
        onSelectFilterCategory,
        onShowFilterList,
        onCloseFilterList,
    } = useFiltering(screenReaderEnabled);

    const channels = useChannels({
        channels: channelsList,
        filterCategory: selectedFilterCategory,
        entitlements,
    });

    useHeaderConfig({
        isRoot: true,
        screenFocused: focused,
    });

    const onFindLiveProgram = useFindLiveProgram(
        startTime,
        programsByChannelsIds,
    );

    const {
        showPinDialog,
        onShowPinDialog,
        onParentalCheckDismiss,
        onParentalControlChecked,
    } = useParentalControl({
        fullscreen,
        onSetPlayerError: () => setErrorExists(true),
        onResetPlayerError: () => setErrorExists(false),
    });

    const handleOnLayout = useCallback(
        ({
            nativeEvent: {
                layout: { y },
            },
        }) => {
            setEpgY(y);
        },
        [],
    );

    const handleSetEpgNotification = useCallback(
        (notification: AppNotification) => {
            if (!epgNotification) {
                setEpgNotification(notification);
            }
        },
        [epgNotification],
    );

    const handleResetNotification = useCallback(() => {
        if (epgNotification) {
            setEpgNotification(undefined);
        }
    }, [epgNotification]);

    // Needed to set the player current playback queue and always use the last selected item
    const setPlayerStateDebounced = useRef(debounce(setPlayerState, 1000));
    const setEpgPlayerSourceDebounced = useRef(
        debounce(setEpgPlayerSource, 500),
    );

    const handleSetEpgPlayerSource = useCallback(
        ({
            channel: _channel,
            program: _program,
            playbackType: _playbackType,
            videoItem: _videoItem,
            instantChange,
        }: {
            channel: RBChannel;
            program: EpgProgram | undefined;
            playbackType: StreamTypes;
            videoItem?: VideoItem;
            instantChange?: boolean;
        }) => {
            const onSetEpgPlayerSource = instantChange
                ? setEpgPlayerSource
                : setEpgPlayerSourceDebounced.current;
            const onSetPlayerState = instantChange
                ? setPlayerState
                : setPlayerStateDebounced.current;

            if (
                channel?.editorial.id !== _channel.editorial.id ||
                errorExists
            ) {
                embeddedPlayerRef.current?.stopPlayback();
            }

            if (_program) {
                epgRef.current?.selectProgram(
                    _channel.editorial.id,
                    _program.id,
                );
            }

            dispatch(startSentryTransaction(SENTRY_TRANSACTIONS.ChannelTune));
            onSetEpgPlayerSource({
                channel: _channel,
                program: _program,
            });

            if (_playbackType) {
                onSetPlayerState({
                    videoItem:
                        // useStartover hook will already provide VideoItem parsed for SO needs.
                        _videoItem ||
                        parseVideoItem({
                            channel: _channel,
                            program: _program,
                        }),
                    playbackType: _playbackType,
                });
            } else {
                onSetPlayerState({
                    videoItem: parseVideoItem({
                        channel: _channel,
                        program: _program,
                    }),
                });
            }
            setSelectedChannelId(_channel.editorial.id);
        },
        [dispatch, channel, errorExists],
    );

    const { getDeepLinkTriggeredParams } = useGetDeepLinkTriggeredParams();
    const { getThirdPartyAppStartParams } = useThirdPartyAppStartParams();

    const {
        selectedDateLabel,
        bottomSheetModalRef,
        handleOpen: handleOpenDatePicker,
        dateData,
        isModalVisible: isDatePickerListVisible,
        handleClose: handleCloseDatePicker,
        modalStartingPosition,
        onPressDatePickerItem,
        previousDateLabel,
        nextDateLabel,
        onPressPrimeTimeJumpMarker,
        onPressPrevDateMarker,
        onPressNextDateMarker,
        onPressNowJumpMarker,
    } = useDatePicker(epgRef, epgY, screenReaderEnabled);

    const {
        onShowFullscreenPlayer,
        onExitFullscreenPlayer,
        onBottomSheetDismiss,
    } = useShowFullscreenPlayer({
        focused,
        webPlayerElementId: `${EMBEDDED_PLAYER_WEB_ID}${route.key}`,
        isDatePickerListVisible,
        isFilterListVisible,
        onCloseDatePicker: handleCloseDatePicker,
        onCloseFilterList,
    });

    const epgConfig = useEpgConfig();

    useAutoScrollOnTimeline({
        epgRef,
        config: epgConfig,
        focused,
        focusToNow,
        channelId: previouslyWatchedChannel,
        isEPGSelectedChannel,
        fullscreen,
        channels,
    });

    const {
        subtitlesDialogVisible,
        subtitleButtonPosition,
        onSubtitlesButtonPress,
        onCloseSubtitlesDialog,
        onSubtitlesModalClosed,
    } = useAudioAndSubtitlesDialog();

    const prevFocused = useRef(focused);
    const prevStatus = useRef(status);

    const clearEpgData = () => {
        setMarkersVisibleDebounced.current?.cancel();
        setPlayerStateDebounced.current?.cancel();
        setEpgPlayerSourceDebounced.current?.cancel();
        setScrollPositionDebounced.current?.cancel();
    };

    useFocusEffect(
        useCallback(() => {
            dispatch(
                endSentryTransaction(SENTRY_TRANSACTIONS.PageNavigationEPG),
            );
            setFocused(true);

            return () => {
                // before navigating to home or to detail pages, clear some data stored in lists and cancel debounce items
                clearEpgData();
                setFocused(false);
                dispatch(setFocusToNow(false));
                setIsEPGSelectedChannel(false);
            };
        }, []),
    );

    const handleEpgSourceOnStatusChange = useCallback(
        async (instantChange?: boolean) => {
            const initialChannel =
                channelsList.find(
                    ({ editorial: { id } }) => previouslyWatchedChannel === id,
                ) ?? channelsList[0];

            const liveProgram = await onFindLiveProgram(initialChannel);

            handleSetEpgPlayerSource({
                channel: initialChannel,
                program: liveProgram,
                playbackType: StreamTypes.live,
                instantChange,
            });
        },
        [channelsList, handleSetEpgPlayerSource, previouslyWatchedChannel],
    );

    const handleRestoreLiveStream = useCallback(() => {
        setTimeout(async () => {
            await handleEpgSourceOnStatusChange(true);
        }, 0);
    }, [handleEpgSourceOnStatusChange]);

    useEffect(() => {
        (async () => {
            // Start the playback at the moment when:
            //  1. EPG screen becomes focused, and epg data was loaded before.
            const focusWithDataLoadedBefore =
                !prevFocused.current && focused && requestSuccess(status);

            //  2. EPG screen was focused before, and epg data was just loaded.
            const focusWithDataJustLoaded =
                focused &&
                appState === 'active' &&
                requestSuccess(status) &&
                !requestSuccess(prevStatus.current) &&
                requestedBy === EpgRequestedBy.SCREEN_ACCESS;

            // 3. EPG date changed from another date to today
            const dateChangedToToday =
                focused &&
                appState === 'active' &&
                requestSuccess(status) &&
                !requestSuccess(prevStatus.current) &&
                requestedBy !== EpgRequestedBy.SCREEN_ACCESS &&
                isToday(secondsToMilliseconds(epgDataStartTime));

            if (
                focusWithDataLoadedBefore ||
                focusWithDataJustLoaded ||
                dateChangedToToday
            ) {
                await handleEpgSourceOnStatusChange();
            }

            if (prevFocused.current && !focused) {
                setEpgPlayerSource(undefined);
            }

            prevFocused.current = focused;
            prevStatus.current = status;
        })();
    }, [
        status,
        focused,
        requestedBy,
        appState,
        epgDataStartTime,
        epgSelectedLayout,
        prevEpgSelectedLayout,
    ]);

    const navigateToDetailPage = useCallback(
        (programData: EpgProgram) => {
            let params: RouteNavigationParams[SCREEN.DETAILS] = {
                templateId,
                filterType: DetailsRequestUrlType.PROGRAM,
                id: programData.id,
                channelId: programData.serviceRef,
            };

            if (programData.seriesRef) {
                const programId = programData.editorial.technicals?.find(
                    ({ deviceType }) =>
                        deviceType.includes(TECHNICAL_DEVICE_TYPE),
                )?.ProgramId;

                params = {
                    ...params,
                    seriesId: programData.seriesRef,
                    season: programData.SeasonNumber,
                    episode: programData.episodeNumber,
                    programId,
                };
            }

            dispatch(
                startSentryTransaction(SENTRY_TRANSACTIONS.DetailPageRendering),
            );
            navigation.navigate(SCREEN.DETAILS, params);
        },
        [templateId],
    );

    useEffect(() => {
        dispatch(endSentryTransaction(SENTRY_TRANSACTIONS.ShowTVGuide));

        return () => {
            setPlayerStateDebounced.current?.cancel();
            setEpgPlayerSourceDebounced.current?.cancel();
        };
    }, []);

    const handleHardwareBackPress = useCallback(() => {
        if (isDatePickerListVisible) {
            handleCloseDatePicker();

            return true;
        }

        if (isFilterListVisible) {
            onCloseFilterList();

            return true;
        }

        return false;
    }, [
        isDatePickerListVisible,
        handleCloseDatePicker,
        isFilterListVisible,
        onCloseFilterList,
    ]);

    useBackHandler(handleHardwareBackPress);

    const programs: EPGPrograms<EpgProgram> = useMemo(() => {
        return channels.reduce((acc, { id: channelId, extra }) => {
            // TODO catchupAllowed must be re-checked during runtime because it depends on Date.now
            const channelSource = channelsById[channelId!];
            const channelPrograms = programsByChannelsIds[channelId];
            const channelSupportsStartover =
                channelSource &&
                channelSource.editorial.startOverSupport &&
                channelSource.technical.startOverSupport;
            const channelSupportsCatchup = isCatchupChannel(channelSource);

            const { subscribed } = extra!;

            const {
                editorial: { tvChannel },
            } = channelSource;

            return {
                ...acc,
                [channelId]: channelPrograms
                    ? channelPrograms.map((item) => {
                          const recordingItem = recordingsByEventId[item.id];
                          const catchupAllowed =
                              channelSupportsCatchup &&
                              isCatchupEpgProgram(item);
                          const startoverAllowed =
                              channelSupportsStartover && item.isStartOver;

                          return parseRBProgramToEpgProgram({
                              t,
                              subscribed,
                              program: item,
                              selectedDate: startTime,
                              catchupAllowed,
                              startoverAllowed,
                              tvChannel,
                              recordingItem,
                          });
                      })
                    : undefined,
            };
        }, {});
    }, [
        t,
        recordingsByEventId,
        channels,
        programsByChannelsIds,
        channelsById,
        startTime,
    ]);

    useEffect(() => {
        (async () => {
            // If channel programs data is loading or not defined,
            // but playback started - highlight program cell in the grid
            // when channel programs data will appear.

            if (
                channel &&
                !prevProgramsByChannelsIds.current[channel.editorial.id] &&
                programsByChannelsIds[channel.editorial.id]
            ) {
                const liveProgram = await onFindLiveProgram(channel);

                if (liveProgram) {
                    if (isWeb) {
                        epgRef.current?.selectProgram(
                            channel.editorial.id,
                            liveProgram.id,
                        );
                    } else {
                        InteractionManager.runAfterInteractions(() => {
                            epgRef.current?.selectProgram(
                                channel.editorial.id,
                                liveProgram.id,
                            );
                        });
                    }
                }
            }

            setTimeout(() => {
                prevProgramsByChannelsIds.current = programsByChannelsIds;
            }, 1000);
        })();
    }, [channel, onFindLiveProgram, programs, programsByChannelsIds]);

    const handleSwitchPlayerSource = useCallback<SwitchPlayerSource>(
        async (newVideoItem?: VideoItem, newPlaybackType?: StreamTypes) => {
            if (newVideoItem && newPlaybackType) {
                const { originalProgram: _program, channelId } = newVideoItem;
                const _channel = channelsById[channelId!];

                if (newPlaybackType === StreamTypes.live) {
                    const liveProgram = await onFindLiveProgram(
                        channelsById[channelId!],
                    );

                    handleSetEpgPlayerSource({
                        channel: _channel,
                        program: liveProgram,
                        playbackType: newPlaybackType,
                        instantChange: true,
                    });
                } else if (newPlaybackType === StreamTypes.startover) {
                    handleSetEpgPlayerSource({
                        channel: _channel,
                        program: _program as EpgProgram | undefined,
                        playbackType: newPlaybackType,
                        videoItem: newVideoItem,
                        instantChange: true,
                    });
                } else {
                    handleSetEpgPlayerSource({
                        channel: _channel,
                        program: _program as EpgProgram | undefined,
                        playbackType: newPlaybackType,
                        videoItem: newVideoItem,
                        instantChange: true,
                    });
                }
            } else {
                // Needed for reseting player source in usePlayerInBackground source resetting.
                setPlayerState({
                    videoItem: undefined,
                    playbackType: StreamTypes.live,
                });
            }
        },
        [handleSetEpgPlayerSource, onFindLiveProgram, channelsById],
    );

    const handleChannelPress = useCallback(
        async (_channel: EPGChannel<EpgChannelExtra>) => {
            const originalChannel = channelsById[_channel.id];
            const liveProgram = await onFindLiveProgram(originalChannel);

            handleSetEpgPlayerSource({
                channel: originalChannel,
                program: liveProgram,
                playbackType: StreamTypes.live,
            });
        },
        [channelsById, onFindLiveProgram, handleSetEpgPlayerSource],
    );

    const handleAccessibilityChannelAction = useCallback(
        async (_channel: EPGChannel<EpgChannelExtra>) => {
            const originalChannel = channelsById[_channel.id];
            const liveProgram = await onFindLiveProgram(originalChannel);

            if (liveProgram) {
                const announcement = new Announcement();

                const isContentLocked = needsToLockContent(
                    profileParentalControlRating,
                    parseParentalRating(liveProgram),
                );

                await announcement.announce(
                    isContentLocked
                        ? t(CARDS_TITLE_CONTENT_LOCKED)
                        : liveProgram.title,
                    false,
                );
            }
        },
        [channelsById, onFindLiveProgram],
    );

    const handleNoDataSelected = useCallback(
        async (channelId: string) => {
            const originalChannel = channelsById[channelId];
            const liveProgram = await onFindLiveProgram(originalChannel);

            if (originalChannel) {
                handleSetEpgPlayerSource({
                    channel: originalChannel,
                    program: liveProgram,
                    playbackType: StreamTypes.live,
                });
            }
        },
        [handleSetEpgPlayerSource, onFindLiveProgram],
    );

    const handleMissingDataCellPress = useCallback(
        async (_channel: EPGChannel<EpgChannelExtra>) => {
            const originalChannel = channelsById[_channel.id];
            const liveProgram = await onFindLiveProgram(originalChannel);

            handleSetEpgPlayerSource({
                channel: originalChannel,
                program: liveProgram,
                playbackType: StreamTypes.live,
            });
        },
        [handleSetEpgPlayerSource, onFindLiveProgram],
    );

    const renderEpgChannelCell = useCallback(
        (_channel: EPGChannel<EpgChannelExtra>) => (
            <EpgChannel
                key={_channel.id}
                channelItem={_channel}
                playing={_channel.id === selectedChannelId}
                isChannelSubscribed={_channel.extra?.subscribed}
            />
        ),
        [selectedChannelId],
    );

    const renderDatePicker = useCallback(
        () => (
            <EpgGridDatePicker
                handleOpen={handleOpenDatePicker}
                selectedDateLabel={selectedDateLabel}
                data={dateData}
                onClose={handleCloseDatePicker}
                isModalVisible={isDatePickerListVisible}
                modalStartingPosition={modalStartingPosition}
                bottomSheetModalRef={bottomSheetModalRef}
                screenReaderEnabled={screenReaderEnabled}
                onPressDatePickerItem={onPressDatePickerItem}
                onDismiss={onBottomSheetDismiss}
            />
        ),
        [
            handleOpenDatePicker,
            selectedDateLabel,
            dateData,
            isDatePickerListVisible,
            handleCloseDatePicker,
            onPressDatePickerItem,
            modalStartingPosition,
            onBottomSheetDismiss,
            screenReaderEnabled,
        ],
    );

    const renderEpgHourCell = useCallback(
        (time: number) => {
            return (
                <EpgHourCell
                    time={time}
                    startTime={startTime}
                    nextDateLabel={nextDateLabel}
                    previousDateLabel={previousDateLabel}
                />
            );
        },
        [startTime, previousDateLabel, nextDateLabel],
    );

    useEffect(() => {
        const now = new Date();

        dispatch(
            setEpgDate({
                start: millisecondsToSeconds(startOfDay(now).getTime()),
                end: millisecondsToSeconds(endOfDay(now).getTime()),
            }),
        );

        return () => {
            dispatch(setFocusToNow(true));
        };
    }, [dispatch]);

    useUpdateEffect(() => {
        const playingChannel = channels.find(
            (ch) => ch.id === selectedChannelId,
        );
        epgRef.current?.scrollToChannel(playingChannel?.id ?? channels[0]?.id);
    }, [selectedFilterCategory]);

    const onHorizontalScroll = useCallback(
        (offsetX: number) => {
            if (markersVisible) {
                setMarkersVisible(false);
            }

            setScrollPositionDebounced.current(offsetX);
            setMarkersVisibleDebounced.current(true);
        },
        [markersVisible],
    );

    const onThirdPartyAppStart = useCallback(
        ({
            appReference,
            deeplinkProviderId,
            deepLinkid,
            providerResourceId,
            appName,
        }: ThirdPartyAppStartItem) => {
            logUavAnalytics(
                getThirdPartyAppStartParams({
                    railId: undefined,
                    playbackType,
                    templateId,
                    appReference,
                    deeplinkProviderId,
                    deepLinkid,
                    providerResourceId,
                    appName,
                }),
            );
        },
        [templateId],
    );

    const onDeepLinkTriggered = useCallback(
        (item: DeepLinkTriggered) => {
            if (templateId) {
                logUavAnalytics(
                    getDeepLinkTriggeredParams({
                        templateId,
                        ...item,
                    }),
                );
            }
        },
        [templateId, getDeepLinkTriggeredParams],
    );

    const handleProgramSelected = useCallback(
        (item: EPGProgram<EpgProgramExtra>) => {
            setIsEPGSelectedChannel(true);
            const programData = programsByChannelsIds[item.channelId].find(
                ({ id }) => id === item.id,
            );

            if (programData && !isLiveEpgProgram(programData)) {
                const ableToPlayCatchup =
                    isCatchupEpgProgram(programData) &&
                    isCapabilityValid(catchupCapability);

                if (
                    !ableToPlayCatchup &&
                    isValidPastDeeplinkItem(programData)
                ) {
                    openDeeplink(
                        programData,
                        onDeepLinkTriggered,
                        onThirdPartyAppStart,
                    );
                } else if (
                    needsToLockContent(
                        profileParentalControlRating,
                        item.extra?.rating,
                    )
                ) {
                    setSelectedProgramData(programData);
                    onShowPinDialog();
                } else {
                    navigateToDetailPage(programData);
                }

                return;
            }

            const _channel = channelsList.find(
                ({ editorial: { id } }) => id === item.channelId,
            );

            if (_channel && programData) {
                handleSetEpgPlayerSource({
                    channel: { ..._channel },
                    program: { ...programData },
                    playbackType: StreamTypes.live,
                });
            }
        },
        [
            channels,
            channelsList,
            handleSetEpgPlayerSource,
            catchupCapability,
            navigateToDetailPage,
            programsByChannelsIds,
            onShowPinDialog,
            profileParentalControlRating,
        ],
    );

    const renderProgramCell = useCallback(
        (
            _program: EPGProgram<EpgProgramExtra>,
            rowIndex: number,
            selected: boolean,
            live: boolean,
        ) => (
            <EpgProgramCell
                program={_program}
                playing={live && _program.channelId === selectedChannelId}
                live={live}
                onAccessibilityTap={handleProgramSelected}
                stickyCellEnabled={epgConfig.stickyPrograms}
            />
        ),
        [selectedChannelId, handleProgramSelected, epgConfig.stickyPrograms],
    );

    const renderMissingDataCell = useCallback(
        (rowIndex: number, selected: boolean, live: boolean) => (
            <EpgMissingDataCell
                playing={live && channels[rowIndex]?.id === selectedChannelId}
                live={live}
                channel={channels[rowIndex]}
                isChannelSubscribed={channels[rowIndex]?.extra?.subscribed}
                onPress={handleMissingDataCellPress}
                stickyCellEnabled={epgConfig.stickyPrograms}
            />
        ),
        [channels, handleMissingDataCellPress, selectedChannelId],
    );

    const renderLoadingRow = useCallback(() => <EpgLoadingRow />, []);

    const renderNoDataRow = useCallback(
        (rowIndex: number) => (
            <EpgNoDataRow
                playing={
                    channels[rowIndex]?.id === selectedChannelId &&
                    isToday(startTime)
                }
                isChannelSubscribed={channels[rowIndex]?.extra?.subscribed}
                handlePress={() => handleNoDataSelected(channels[rowIndex]?.id)}
            />
        ),
        [channels, selectedChannelId, startTime],
    );

    const renderNowLine = useCallback(
        () => <EpgNowLine visible={!!channels.length} />,
        [channels.length],
    );

    const needProgramsForChannels = useCallback(
        (
            epgChannelsWithoutData: EPGChannels<EpgChannelExtra>,
            priority: EPGRequestPriority,
        ) => {
            if (priority === EPGRequestPriority.high) {
                dispatch(
                    getEpgByVisibleChannels({
                        channels: epgChannelsWithoutData.map(({ id }) => id),
                    }),
                );

                return true;
            }
            return false;
        },
        [],
    );

    // Flag to "debounce" frequent epg date change by "<>" web buttons, which
    // gives unexpected epg grid behaviour due to async needsProgramsForChannels call.
    const disabledArrowNavigation = useRef<boolean>(false);
    const disabledArrowNavigationTimeout = useRef<NodeJS.Timeout>(undefined);
    const prevNewDayIsLoading = usePrevious(newDayIsLoading);

    useEffect(() => {
        if (
            !prevNewDayIsLoading &&
            newDayIsLoading &&
            disabledArrowNavigationTimeout.current
        ) {
            clearTimeout(disabledArrowNavigationTimeout.current);
            disabledArrowNavigationTimeout.current = undefined;
        }

        if (prevNewDayIsLoading && !newDayIsLoading) {
            setTimeout(() => {
                disabledArrowNavigation.current = false;
            }, 600);
        }
    }, [newDayIsLoading]);

    const handleTimeBarArrowPress = useCallback(
        (type: EArrowType, shouldDateChange: boolean) => {
            if (
                !disabledArrowNavigation.current &&
                type === EArrowType.BWD &&
                epgConfig.hourWidth
            ) {
                if (scrollPosition === 0 && shouldDateChange) {
                    disabledArrowNavigation.current = true;
                    disabledArrowNavigationTimeout.current = setTimeout(() => {
                        disabledArrowNavigation.current = false;
                    }, 1000);
                    onPressPrevDateMarker();
                } else {
                    epgRef.current?.scrollByTime(-2 * 60 * 60 * 1000);
                }
            } else if (
                !disabledArrowNavigation.current &&
                type === EArrowType.FWD &&
                epgConfig.hourWidth
            ) {
                if (
                    Math.floor(scrollPosition) ===
                        Math.floor(
                            epgConfig.hourWidth * epgConfig.hoursCount -
                                epgConfig.hourWidth * HOURS_IN_VIEWPORT,
                        ) &&
                    shouldDateChange
                ) {
                    disabledArrowNavigation.current = true;
                    disabledArrowNavigationTimeout.current = setTimeout(() => {
                        disabledArrowNavigation.current = false;
                    }, 1000);
                    onPressNextDateMarker();
                } else {
                    epgRef.current?.scrollByTime(2 * 60 * 60 * 1000);
                }
            }
        },
        [
            epgConfig.hourWidth,
            epgConfig.hoursCount,
            scrollPosition,
            onPressNextDateMarker,
            onPressPrevDateMarker,
        ],
    );

    const handleCloseParentalControl = useCallback(() => {
        onParentalCheckDismiss(isLive(selectedProgramData));
        setSelectedProgramData(null);
    }, [onParentalCheckDismiss, selectedProgramData]);

    const handleParentalControlChecked = useCallback(() => {
        if (selectedProgramData) {
            navigateToDetailPage(selectedProgramData);
        }

        onParentalControlChecked();
        setSelectedProgramData(null);
    }, [selectedProgramData, navigateToDetailPage, onParentalControlChecked]);

    const handleVerticalScroll = useCallback((yOffset: number) => {
        epgRef.current?.scrollToYOffset(yOffset);
    }, []);

    const SkeletonLoadingComponent = useMemo(() => {
        if (isWeb) {
            return EpgDesktopSkeletonLoading;
        }

        if (isTablet) {
            return EPGSkeletonLoadingTablet;
        }

        return EPGSkeletonLoadingMobile;
    }, []);

    const pageErrorTextRef = useRef<NativeText>(null);

    useEffect(() => {
        if (requestFailure(status) && pageErrorTextRef.current) {
            setAccessibilityFocusTo(pageErrorTextRef);
        }
    }, [status]);

    useEffect(() => {
        if (
            ((prevEpgSelectedLayout && epgSelectedLayout === EpgLayout.GRID) ||
                prevScreenReaderEnabled === screenReaderEnabled) &&
            channels.length
        ) {
            needProgramsForChannels([channels[0]], EPGRequestPriority.high);
        }
    }, [
        epgSelectedLayout,
        prevEpgSelectedLayout,
        screenReaderEnabled,
        prevScreenReaderEnabled,
    ]);

    if (requestFailure(status)) {
        return (
            <SafeAreaView
                edges={edges}
                style={[
                    gStyles.flex,
                    gStyles.centerItems,
                    {
                        backgroundColor: background,
                    },
                ]}
                testID={FALLBACK_MESSAGE_ID}
            >
                <Text
                    ref={pageErrorTextRef}
                    category={ETextCategory.body1}
                    color={ETextColor.neutral}
                    center
                    style={styles.emptyPageError}
                >
                    {t('EMPTY_CONTENT_PAGES_GLOBAL')}
                </Text>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.safeAreaView} edges={edges}>
            <GestureHandlerRootView style={styles.gestureHandlerRootView}>
                {isTablet && (
                    <View
                        style={styles.safeZone}
                        dataSet={underlayingPlayerModalElementsDataSet}
                    />
                )}
                <View style={styles.playerContainer}>
                    <EmbeddedPlayer
                        ref={embeddedPlayerRef}
                        style={styles.player}
                        videoItem={videoItem}
                        playbackType={playbackType}
                        appState={appState}
                        navigation={navigation}
                        fullscreen={fullscreen}
                        focused={focused}
                        channel={channel}
                        templateId={templateId}
                        subtitlesDialogVisible={subtitlesDialogVisible}
                        subtitleButtonPosition={subtitleButtonPosition}
                        primaryColor={primary}
                        screenReaderEnabled={screenReaderEnabled}
                        onSubtitlesButtonPress={onSubtitlesButtonPress}
                        onCloseSubtitlesDialog={onCloseSubtitlesDialog}
                        onSubtitlesModalClosed={onSubtitlesModalClosed}
                        onSetNotification={handleSetEpgNotification}
                        onSwitchPlayerSource={handleSwitchPlayerSource}
                        onShowFullscreen={onShowFullscreenPlayer}
                        onSetErrorExists={setErrorExists}
                        onSetPlayerState={setPlayerState}
                        onCloseFullscreen={onExitFullscreenPlayer}
                        userRating={profileParentalControlRating}
                        profileId={selectedProfileId}
                        handleRestoreLiveStream={handleRestoreLiveStream}
                    />
                </View>
                <View
                    style={[gStyles.flex, isWeb && styles.webEpgContainer]}
                    onLayout={handleOnLayout}
                    // @ts-ignore
                    draggable="false"
                    importantForAccessibility={
                        fullscreen ? 'no-hide-descendants' : 'auto'
                    }
                    accessibilityElementsHidden={fullscreen}
                    dataSet={underlayingPlayerModalElementsDataSet}
                >
                    <EPG
                        appState={appState}
                        key={epgConfig.hoursCount}
                        style={gStyles.flex}
                        ref={epgRef}
                        config={epgConfig}
                        channels={channels}
                        programs={programs}
                        onHorizontalScroll={onHorizontalScroll}
                        startTime={startTime}
                        renderChannelCell={renderEpgChannelCell}
                        renderHourCell={renderEpgHourCell}
                        renderProgramCell={renderProgramCell}
                        renderMissingDataCell={renderMissingDataCell}
                        renderLoadingRow={renderLoadingRow}
                        renderNoDataRow={renderNoDataRow}
                        renderNowLine={renderNowLine}
                        renderTopLeftAccessoryView={renderDatePicker}
                        onProgramSelected={handleProgramSelected}
                        onChannelPress={handleChannelPress}
                        onChannelAccessibilityAction={
                            handleAccessibilityChannelAction
                        }
                        needProgramsForChannels={needProgramsForChannels}
                        screenReaderEnabled={screenReaderEnabled}
                    />
                    <EpgJumpMarkers
                        selectedDate={startTime}
                        visible={markersVisible && requestSuccess(status)}
                        config={epgConfig}
                        scrollPos={scrollPosition}
                        nextDateLabel={nextDateLabel}
                        prevDateLabel={previousDateLabel}
                        onPressNowJumpMarker={onPressNowJumpMarker}
                        onPressPrimeTimeJumpMarker={onPressPrimeTimeJumpMarker}
                        onPressNextDateJumpMarker={onPressNextDateMarker}
                        onPressPrevDateJumpMarker={onPressPrevDateMarker}
                    />
                    {isWeb && (
                        <EpgTimeBarArrows
                            startTime={startTime}
                            handleTimeBarArrowPress={handleTimeBarArrowPress}
                        />
                    )}
                    {!channels.length && (
                        <View
                            style={[
                                styles.noChannelsContainer,
                                {
                                    top: epgConfig.rowHeight,
                                },
                            ]}
                        >
                            <Text
                                style={styles.noChannelsText}
                                category={ETextCategory.body1}
                                color={ETextColor.light_grey}
                                center
                            >
                                {t('EMPTY_CONTENT_EPG_FILTER')}
                            </Text>
                        </View>
                    )}
                    <EpgFilter
                        bottomSheetRef={filterBottomSheetRef}
                        filterCategories={filterCategories}
                        filterCategorySelected={filterCategorySelected}
                        isFilterListVisible={isFilterListVisible}
                        screenReaderEnabled={screenReaderEnabled}
                        onPressFilter={onShowFilterList}
                        onCloseFilter={onCloseFilterList}
                        onPressFilterCategoryItem={onSelectFilterCategory}
                        onDismiss={onBottomSheetDismiss}
                    />
                </View>
                <Notification
                    notification={epgNotification}
                    onResetNotification={handleResetNotification}
                />
            </GestureHandlerRootView>
            {!requestSuccess(status) &&
                requestedBy === EpgRequestedBy.SCREEN_ACCESS && (
                    <SkeletonLoading
                        isScreenSkeleton
                        SkeletonLoadingComponent={SkeletonLoadingComponent}
                        backgroundColor={background}
                        width={widthPercentageToDP(360, 960, 1136)}
                        height={heightPercentageToDP(640, 640, 656)}
                        edges={edges}
                        testID={EPG_PROGRAM_CELLS_LOADING_ID}
                    />
                )}

            <GenericPinDialog
                modal
                appState={appState}
                visible={showPinDialog}
                onClose={handleCloseParentalControl}
                onSuccess={handleParentalControlChecked}
            />
            {isWeb && (
                <ScrollArrowButtons
                    viewId={`verticalScrollContainer-${route.key}`}
                    onPress={handleVerticalScroll}
                />
            )}
        </SafeAreaView>
    );
}

export default EpgGridContent;
